/* One reading of a refused response, for every client in this directory.
 *
 * Every refusal the API sends is 15 section 1.5's `{error, detail, request_id}`
 * (`app/errors.py`), and `detail` is a usable sentence - the upstream request
 * id a support question needs, in the case of the embed mint (`app/embeds.py`).
 * A client that discards it shows a status code where a cause was available.
 *
 * Here rather than per resource because the three steps are the same wherever
 * a refusal is read: prefer the body's `detail`, fall back to the status the
 * caller already knows, and never let a non-JSON body turn one handled refusal
 * into a second failure. `envelope.ts` is the same move for cursor logic.
 */

/** The `detail` of a refused response, or `fallback` when it carries none.
 *
 * Always a string, validation refusals included: `app/errors.py` joins
 * FastAPI's list of errors into one sentence before it leaves the API, so a
 * body whose `detail` is anything else is not ours and gets the fallback.
 */
export async function refusalDetail(
  response: Response,
  fallback: string,
): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === "object" && "detail" in body) {
      const detail: unknown = (body as { detail: unknown }).detail;
      if (typeof detail === "string" && detail) return detail;
    }
  } catch {
    // A non-JSON body is not worth a second failure mode; the status the
    // caller put in `fallback` is the information.
  }
  return fallback;
}
